import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

const ProjectCard = ({ project, index }) => {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const [hovered, setHovered] = useState(false);
    const projectLink = `/projects/${project.slug || project.id}`;

    // Handle multiple categories for display
    const categoryDisplay = Array.isArray(project.category)
        ? project.category.join(' & ')
        : project.category;

    return (
        <Link to={projectLink} className="block group">
            <motion.div
                ref={ref}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative overflow-hidden rounded-lg shadow-lg bg-white h-full"
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <div className="relative h-64 w-full">
                    <img
                        src={project.mainImage || project.image}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => { e.target.onerror = null; e.target.src="/projects/placeholder_fallback.jpg" }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-70 group-hover:opacity-80 transition-opacity duration-300"></div>

                    {/* Status badge */}
                    {project.status && (
                        <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-medium ${
                            project.status === 'Work In Progress'
                                ? 'bg-blue-500 text-white'
                                : 'bg-green-500 text-white'
                        }`}>
                            {project.status === 'Work In Progress' ? 'In Progress' : 'Completed'}
                        </div>
                    )}
                </div>

                <motion.div
                    className="absolute bottom-0 left-0 right-0 p-5 text-white"
                    initial={{ y: 30, opacity: 0 }}
                    animate={hovered ? { y: 0, opacity: 1 } : { y: 30, opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    style={{ pointerEvents: 'none' }}
                >
                    <span className="text-arcdom-gold text-xs font-semibold mb-1 block uppercase tracking-wider">
                        {categoryDisplay} | {project.year}
                    </span>
                    <h3 className="text-lg font-bold mb-2 line-clamp-2">{project.title}</h3>
                    <div className="text-arcdom-gold inline-flex items-center text-sm font-medium">
                        View Project
                        <svg className="w-4 h-4 ml-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                </motion.div>

                <div
                    className={`absolute bottom-0 left-0 right-0 p-5 text-white transition-opacity duration-300 ${hovered ? 'opacity-0' : 'opacity-100'}`}
                    style={{ pointerEvents: 'none' }}
                >
                    <span className="text-arcdom-taupe text-xs font-semibold mb-1 block uppercase tracking-wider">
                        {categoryDisplay} | {project.year}
                    </span>
                    <h3 className="text-lg font-bold line-clamp-2">{project.title}</h3>
                </div>
            </motion.div>
        </Link>
    );
};

export default ProjectCard;
